import React, { useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Briefcase, MapPin, Clock, ArrowRight } from 'lucide-react';
import Header from './Header';
import Footer from './Footer';
import PageHero from './ui/PageHero';
import { useNavigate } from 'react-router-dom';
import { jobPostings } from '../jobPostings';
import SEO from './SEO';
import seoData from '../seoData';

const JobsCareers = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <SEO title={seoData.careers?.title} description={seoData.careers?.description} path="/careers" keywords={seoData.careers?.keywords || []} />

      <PageHero
        title="Join Our Team"
        description="Build your career with a leader in industrial coating and repair services. We're looking for skilled, dedicated professionals to join our growing team."
        backgroundKey="careers"
        showButton={false}
      />

      {/* Why Work With Us Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Why Work at BCS?</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="text-slate-900">Competitive Compensation</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">Industry-leading pay, performance bonuses, and comprehensive benefits package including health, dental, and vision coverage.</p>
              </CardContent>
            </Card>
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="text-slate-900">Career Growth</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">Ongoing training, certification support, and clear advancement opportunities within a growing company.</p>
              </CardContent>
            </Card>
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="text-slate-900">Great Culture</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">Work with experienced professionals who value quality, safety, and teamwork. We support work-life balance.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Job Postings Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Current Openings</h2>
          <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
            Explore our current job opportunities. Don't see the right fit? Send us your resume – we're always looking for talented individuals.
          </p>
          
          <div className="space-y-6">
            {jobPostings.map((job) => (
              <Card key={job.id} className="hover:shadow-lg transition-shadow bg-white border-slate-200">
                <CardHeader>
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                    <div>
                      <CardTitle className="text-2xl text-slate-900 mb-2">{job.title}</CardTitle>
                      <div className="flex flex-wrap gap-3 text-sm text-gray-600">
                        <div className="flex items-center">
                          <MapPin size={16} className="mr-1" />
                          {job.location}
                        </div>
                        <div className="flex items-center">
                          <Briefcase size={16} className="mr-1" />
                          {job.type}
                        </div>
                        <div className="flex items-center">
                          <Clock size={16} className="mr-1" />
                          {job.experience}
                        </div>
                      </div>
                    </div>
                    <Button 
                      className="bg-blue-900 hover:bg-blue-800 whitespace-nowrap"
                      onClick={() => window.location.href = 'mailto:info@bcs-us.com?subject=Job Application - ' + job.title}
                    >
                      Apply Now
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700 mb-6">{job.description}</p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="font-semibold text-slate-900 mb-3">Responsibilities:</h4>
                      <ul className="space-y-2 text-gray-600">
                        {job.responsibilities.map((resp, index) => (
                          <li key={index} className="flex items-start">
                            <span className="text-blue-900 mr-2">•</span>
                            <span>{resp}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    
                    <div>
                      <h4 className="font-semibold text-slate-900 mb-3">Qualifications:</h4>
                      <ul className="space-y-2 text-gray-600">
                        {job.qualifications.map((qual, index) => (
                          <li key={index} className="flex items-start">
                            <span className="text-blue-900 mr-2">•</span>
                            <span>{qual}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-blue-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Join Our Team?</h2>
          <p className="text-xl text-blue-100 mb-8">
            Submit your resume and tell us why you'd be a great fit for BCS.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              className="bg-white text-blue-900 hover:bg-gray-100"
              onClick={() => window.location.href = 'mailto:info@bcs-us.com?subject=Career Inquiry'}
            >
              Email Your Resume
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              className="border-white text-white hover:bg-blue-800"
              onClick={() => window.location.href = 'tel:+19493719948'}
            >
              Call (949) 371-9948
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default JobsCareers;
